import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL = "https://wa.me/919999999999?text=Hi%2C%20I%20just%20submitted%20the%20POD%20lead%20form%20and%20want%20to%20start%20my%20brand";

const CHANNELS = ["Shopify", "Instagram", "Meesho", "Amazon", "Flipkart", "Own Website", "Offline", "Not Decided Yet"];
const START_WHEN = ["Immediately", "Within 1 Month", "Within 3 Months", "Just Exploring"];
const WEBSITE = ["Yes", "Under Development", "Planning to Build One", "No"];
const READINESS = ["Yes, ready to print", "Some designs", "Need help creating designs", "Need complete brand & design support"];
const TARGET = ["1–50", "50–200", "200–500", "500+"];
const HEARD = ["Google", "Instagram", "Facebook", "YouTube", "Friend/Referral", "Other"];

const initial = {
  full_name: "",
  mobile: "",
  email: "",
  start_when: "",
  channels: [],
  has_website: "",
  design_readiness: "",
  sold_online_before: "",
  monthly_target: "",
  heard_from: "",
};

export default function StartPodLeadForm({ open, onClose, calcContext = {}, ctaLabel = "" }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const TOTAL_STEPS = 6;

  useEffect(() => {
    if (!open) return;
    setStep(1); setForm(initial); setDone(false); setSubmitting(false);
    // Analytics
    if (typeof window !== "undefined") {
      if (window.gtag) window.gtag("event", "lead_form_open", { cta: ctaLabel });
      if (window.fbq) window.fbq("track", "InitiateCheckout");
    }
  }, [open, ctaLabel]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChannel = (c) => setForm((f) => ({ ...f, channels: f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c] }));

  const stepValid = () => {
    if (step === 1) return form.mobile.replace(/\D/g, "").length >= 8 && /@/.test(form.email);
    return true; // other steps optional
  };

  const next = () => {
    if (!stepValid()) return;
    if (step < TOTAL_STEPS) setStep((s) => s + 1);
    else submit();
  };
  const back = () => step > 1 && setStep((s) => s - 1);

  const submit = async () => {
    setSubmitting(true);
    try {
      const utm = new URLSearchParams(window.location.search);
      const utmObj = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
        if (utm.get(k)) utmObj[k] = utm.get(k);
      });
      await axios.post(`${API}/leads`, { ...form, calc: calcContext, utm: utmObj });
      setDone(true);
      if (typeof window !== "undefined") {
        if (window.gtag) window.gtag("event", "generate_lead", { value: 1, currency: "INR" });
        if (window.fbq) window.fbq("track", "Lead");
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1200] bg-slate-950/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={onClose}
        data-testid="lead-form-modal"
      >
        <motion.div
          initial={{ y: 60, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header w/ progress */}
          <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 text-white px-6 py-5 relative">
            <button onClick={onClose} data-testid="lead-form-close" className="absolute top-4 right-4 p-1.5 rounded-full bg-white/15 hover:bg-white/25"><X className="w-4 h-4" /></button>
            {!done && <>
              <p className="text-[11px] uppercase tracking-widest font-bold opacity-80">Step {step} of {TOTAL_STEPS}</p>
              <div className="mt-2 h-1.5 bg-white/20 rounded-full overflow-hidden">
                <motion.div className="h-full bg-white" initial={false} animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }} transition={{ duration: 0.35 }} />
              </div>
              <p className="mt-3 text-lg font-extrabold">{[
                "Let's get you started",
                "When do you want to launch?",
                "Where will you sell?",
                "Do you have a website?",
                "How ready are your designs?",
                "A little about your business",
              ][step - 1]}</p>
            </>}
            {done && <p className="text-lg font-extrabold">You're in! 🎉</p>}
          </div>

          {/* Body */}
          <div className="p-6 min-h-[280px]">
            {done ? (
              <div className="text-center py-6 space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-9 h-9 text-emerald-600" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">Thank you!</h3>
                <p className="text-slate-600">Our POD specialist will contact you within <b>24 hours</b>. Want to move faster?</p>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="lead-form-whatsapp" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-6 py-3 rounded-full transition">Chat on WhatsApp →</a>
              </div>
            ) : (
              <>
                {step === 1 && (
                  <div className="space-y-3">
                    <Field label="Full name">
                      <input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="lead-full-name" placeholder="Your name" className="input" />
                    </Field>
                    <Field label="Mobile number *">
                      <input value={form.mobile} onChange={(e) => update("mobile", e.target.value.replace(/[^\d+]/g, ""))} data-testid="lead-mobile" placeholder="+91 98765 43210" className="input" />
                    </Field>
                    <Field label="Email address *">
                      <input value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="lead-email" placeholder="you@example.com" type="email" className="input" />
                    </Field>
                    <p className="text-[11px] text-slate-400">We only contact you about your POD setup. Never shared.</p>
                  </div>
                )}
                {step === 2 && (<Chips options={START_WHEN} value={form.start_when} onChange={(v) => update("start_when", v)} testid="lead-when" />)}
                {step === 3 && (<Multi options={CHANNELS} values={form.channels} toggle={toggleChannel} testid="lead-channels" />)}
                {step === 4 && (<Chips options={WEBSITE} value={form.has_website} onChange={(v) => update("has_website", v)} testid="lead-website" />)}
                {step === 5 && (<Chips options={READINESS} value={form.design_readiness} onChange={(v) => update("design_readiness", v)} testid="lead-designs" />)}
                {step === 6 && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-bold text-slate-600 uppercase mb-2">Sold online before?</p>
                      <Chips options={["Yes", "No"]} value={form.sold_online_before} onChange={(v) => update("sold_online_before", v)} testid="lead-sold" small />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-600 uppercase mb-2">Target monthly orders</p>
                      <Chips options={TARGET} value={form.monthly_target} onChange={(v) => update("monthly_target", v)} testid="lead-target" small />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-600 uppercase mb-2">How did you hear about us?</p>
                      <Chips options={HEARD} value={form.heard_from} onChange={(v) => update("heard_from", v)} testid="lead-heard" small />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!done && (
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
              <button onClick={back} disabled={step === 1} data-testid="lead-form-back" className="text-sm font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-30 flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Back</button>
              <button
                onClick={next}
                disabled={!stepValid() || submitting}
                data-testid="lead-form-next"
                className="bg-slate-950 hover:bg-slate-800 text-white font-bold px-6 py-3 rounded-full text-sm flex items-center gap-1.5 disabled:opacity-40 shadow-lg transition"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : (step === TOTAL_STEPS ? <>Finish & get a callback →</> : <>Continue <ChevronRight className="w-4 h-4" /></>)}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
      <style>{`.input { width:100%; border:1.5px solid #e2e8f0; border-radius:12px; padding:12px 14px; font-size:14px; outline:none; transition:border-color .15s, box-shadow .15s; }
        .input:focus { border-color:#6366f1; box-shadow:0 0 0 4px rgba(99,102,241,.15); }`}</style>
    </AnimatePresence>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Chips({ options, value, onChange, testid, small }) {
  return (
    <div className={`grid ${small ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          data-testid={`${testid}-${o.replace(/\s+/g, "-").toLowerCase()}`}
          className={`text-left px-4 py-3 rounded-xl border-2 font-medium transition ${value === o ? "border-indigo-600 bg-indigo-50 text-indigo-800" : "border-slate-200 hover:border-slate-400 text-slate-700"}`}
        >{o}</button>
      ))}
    </div>
  );
}

function Multi({ options, values, toggle, testid }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((o) => {
        const on = values.includes(o);
        return (
          <button key={o} onClick={() => toggle(o)} data-testid={`${testid}-${o.replace(/\s+/g, "-").toLowerCase()}`}
            className={`text-left px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition ${on ? "border-indigo-600 bg-indigo-50 text-indigo-800" : "border-slate-200 hover:border-slate-400 text-slate-700"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}
